import React from "react"
import PropTypes from "prop-types"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, LineChart, Line, CartesianGrid } from "recharts"
import { formatCurrency, formatNumber, formatPercent, colorPara } from "../helpers"

const TooltipPie = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const d = payload[0].payload
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
      <div style={{ fontWeight: 700 }}>{d.nombre}</div>
      <div>{formatCurrency(d.valor)}</div>
      <div style={{ color: "#94a3b8", fontSize: 11 }}>{formatNumber(d.transacciones)} tx · {formatPercent(d.pct)}</div>
    </div>
  )
}

TooltipPie.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const TooltipDia = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.fill || p.stroke }}>
          {p.name}: {formatCurrency(p.value)}
        </div>
      ))}
    </div>
  )
}

TooltipDia.propTypes = { active: PropTypes.bool, payload: PropTypes.array, label: PropTypes.string }

const PercentilCard = ({ label, value }) => (
  <div style={{ flex: "1 1 120px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 16, color: "#0f172a", fontWeight: 700, marginTop: 2 }}>{formatCurrency(value)}</div>
  </div>
)

PercentilCard.propTypes = { label: PropTypes.string, value: PropTypes.number }

const MetodosTab = ({ data }) => {
  const topMetodos = data?.top_bottom?.top_metodos || []
  const distribucion = data?.distribucion || {}
  const tendencia = data?.tendencia_diaria || []

  // Para tendencia: agrupar por fecha sumando todas las sucursales/métodos
  const tendenciaPorFecha = (() => {
    const map = new Map()
    tendencia.forEach(t => {
      const f = t.fecha
      const prev = map.get(f) || { fecha: f, valor: 0, transacciones: 0 }
      prev.valor += Number(t.total_valor || t.valor) || 0
      prev.transacciones += Number(t.transacciones) || 0
      map.set(f, prev)
    })
    return Array.from(map.values()).sort((a, b) => a.fecha.localeCompare(b.fecha))
  })()

  const porDiaSemana = (distribucion.por_dia_semana || []).slice().sort((a, b) =>
    (a.numero_dia || 0) - (b.numero_dia || 0),
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Top métodos: pie + tabla */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
          <i className="mdi mdi-credit-card-multiple me-2 text-primary" />
          Distribución por método de pago
        </h6>
        {topMetodos.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Sin datos.</div>
        ) : (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 380px", minWidth: 320, height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={topMetodos} dataKey="valor" nameKey="nombre"
                    cx="50%" cy="50%" outerRadius={110} innerRadius={50} paddingAngle={1}
                  >
                    {topMetodos.map((_, i) => <Cell key={i} fill={colorPara(i)} stroke="#fff" strokeWidth={2} />)}
                  </Pie>
                  <Tooltip content={<TooltipPie />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: "1 1 380px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Método</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Tx</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Valor</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {topMetodos.map((m, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 12px", color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: colorPara(i) }} />
                        {m.nombre}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>{formatNumber(m.transacciones)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>{formatCurrency(m.valor)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>{formatPercent(m.pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Tendencia diaria */}
      {tendenciaPorFecha.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
            <i className="mdi mdi-chart-line me-2 text-primary" />
            Tendencia diaria
          </h6>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={tendenciaPorFecha} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
                <Tooltip content={<TooltipDia />} />
                <Line type="monotone" dataKey="valor" name="Valor" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Por día de semana + percentiles */}
      {(porDiaSemana.length > 0 || distribucion.ticket_p50 != null) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {porDiaSemana.length > 0 && (
            <div style={{ flex: "1 1 480px", background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0", minWidth: 0 }}>
              <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
                <i className="mdi mdi-calendar-week me-2 text-primary" />
                Por día de la semana
              </h6>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={porDiaSemana}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="dia" tick={{ fontSize: 11, fill: "#475569" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
                    <Tooltip content={<TooltipDia />} />
                    <Bar dataKey="valor" name="Valor" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", justifyContent: "space-around", marginTop: 8, fontSize: 12 }}>
                <span style={{ color: "#10b981" }}><i className="mdi mdi-check me-1" />Mejor: <strong>{distribucion.dia_semana_mejor || "—"}</strong></span>
                <span style={{ color: "#ef4444" }}><i className="mdi mdi-close me-1" />Peor: <strong>{distribucion.dia_semana_peor || "—"}</strong></span>
              </div>
            </div>
          )}

          {distribucion.ticket_p50 != null && (
            <div style={{ flex: "1 1 320px", background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
              <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
                <i className="mdi mdi-chart-box-outline me-2 text-primary" />
                Distribución del ticket (percentiles)
              </h6>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <PercentilCard label="P25" value={distribucion.ticket_p25} />
                <PercentilCard label="Mediano (P50)" value={distribucion.ticket_p50} />
                <PercentilCard label="P75" value={distribucion.ticket_p75} />
                <PercentilCard label="P95" value={distribucion.ticket_p95} />
              </div>
              {distribucion.coef_variacion_diaria != null && (
                <div style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
                  Coef. de variación diaria: <strong style={{ color: "#0f172a" }}>{formatPercent(distribucion.coef_variacion_diaria)}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

MetodosTab.propTypes = { data: PropTypes.object }

export default MetodosTab
